import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAvailableParkingUnits,
  getUnitParkingLinks
} from '../../services/api/implementation/impl';

const KIND_OPTIONS = [
  { value: 'all', label: 'All parking' },
  { value: '2-wheeler', label: '2-Wheeler' },
  { value: '4-wheeler', label: '4-Wheeler' }
];

/**
 * Allot one or more parking units to a building flat.
 * Parent owns persist (save) — this panel only manages selection state.
 */
export default function ParkingAllotmentPanel({
  projectId,
  flatUnitId,
  defaultIncludeCost = false,
  value,
  onChange,
  disabled = false,
  compact = false,
  /** 'flat' = allot parking to this unit; 'parking' = show linked flat only */
  mode = 'flat'
}) {
  const [kind, setKind] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [includeCostById, setIncludeCostById] = useState({});
  const [parkingCostById, setParkingCostById] = useState({});

  const { data: linksData, isLoading: linksLoading } = useQuery({
    queryKey: ['unitParkingLinks', flatUnitId],
    queryFn: () => getUnitParkingLinks(flatUnitId),
    enabled: !!flatUnitId
  });

  const { data: available = [], isLoading: availableLoading } = useQuery({
    queryKey: ['availableParking', projectId, flatUnitId, kind],
    queryFn: () => getAvailableParkingUnits(projectId, flatUnitId, kind),
    enabled: !!projectId && mode === 'flat'
  });

  // Seed from server links once (or whenever flat changes)
  useEffect(() => {
    if (mode !== 'flat' || !flatUnitId || !linksData) return;
    if (Array.isArray(value?.parking_unit_ids)) return; // controlled externally already

    const linked = linksData.linkedParking || [];
    const ids = linked.map((l) => l.parking_unit_id || l.parkingUnit?.id).filter(Boolean);
    const costs = {};
    const unitCosts = {};
    linked.forEach((l) => {
      const id = l.parking_unit_id || l.parkingUnit?.id;
      if (id) {
        costs[id] = !!l.include_cost;
        unitCosts[id] = Number(l.parkingUnit?.cost) || 0;
      }
    });
    setSelectedIds(ids);
    setIncludeCostById(costs);
    setParkingCostById(unitCosts);
    onChange?.({
      parking_unit_ids: ids,
      include_cost_by_parking_id: costs,
      parking_costs_by_id: unitCosts,
      linkedFlat: linksData.linkedFlat || null
    });
  }, [flatUnitId, linksData, mode]);

  // Controlled sync
  useEffect(() => {
    if (!value) return;
    if (Array.isArray(value.parking_unit_ids)) {
      setSelectedIds(value.parking_unit_ids.map(Number));
    }
    if (value.include_cost_by_parking_id) {
      setIncludeCostById(value.include_cost_by_parking_id);
    }
    if (value.parking_costs_by_id) {
      setParkingCostById(value.parking_costs_by_id);
    }
  }, [value]);

  const linkedFlat = linksData?.linkedFlat || value?.linkedFlat || null;

  const optionMap = useMemo(() => {
    const map = new Map();
    (available || []).forEach((p) => map.set(Number(p.id), p));
    (linksData?.linkedParking || []).forEach((l) => {
      const p = l.parkingUnit;
      if (p?.id) map.set(Number(p.id), p);
    });
    return map;
  }, [available, linksData]);

  const emit = (ids, costs, unitCosts = parkingCostById) => {
    setSelectedIds(ids);
    setIncludeCostById(costs);
    setParkingCostById(unitCosts);
    onChange?.({
      parking_unit_ids: ids,
      include_cost_by_parking_id: costs,
      parking_costs_by_id: unitCosts,
      linkedFlat
    });
  };

  const toggleParking = (parkingId) => {
    if (disabled) return;
    const id = Number(parkingId);
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    const nextCosts = { ...includeCostById };
    const nextUnitCosts = { ...parkingCostById };
    if (!selectedIds.includes(id)) {
      nextCosts[id] = includeCostById[id] ?? defaultIncludeCost;
      nextUnitCosts[id] = Number(optionMap.get(id)?.cost) || parkingCostById[id] || 0;
    } else {
      delete nextCosts[id];
      delete nextUnitCosts[id];
    }
    emit(next, nextCosts, nextUnitCosts);
  };

  const toggleIncludeCost = (parkingId) => {
    if (disabled) return;
    const id = Number(parkingId);
    const nextCosts = {
      ...includeCostById,
      [id]: !includeCostById[id]
    };
    emit(selectedIds, nextCosts, parkingCostById);
  };

  // Parking unit view: show linked flat only
  if (mode === 'parking') {
    const flat = linkedFlat?.flatUnit || linkedFlat;
    return (
      <div className={`${compact ? '' : 'col-span-2'} rounded border border-stone-200 bg-stone-50 p-3`}>
        <div className="text-sm font-medium text-gray-700 mb-1">Linked flat</div>
        {linkedFlat ? (
          <>
            <div className="text-sm text-gray-800">
              {flat?.unitName || `Unit #${linkedFlat.flat_unit_id}`}
              {flat?.blockName ? ` · ${flat.blockName}` : ''}
              {flat?.levelName ? ` · ${flat.levelName}` : ''}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Unlink from the flat unit editor to free this parking.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500">Not linked to any flat — still free to allot.</p>
        )}
      </div>
    );
  }

  const loading = linksLoading || availableLoading;

  return (
    <div className={`${compact ? '' : 'col-span-2'} rounded border border-stone-200 bg-stone-50 p-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-medium text-gray-700">Linked parking</div>
          <p className="text-xs text-gray-500">
            Allot one or more free parking spaces. Already-allotted parking stays blocked.
          </p>
        </div>
        <div className="flex gap-1">
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => setKind(opt.value)}
              className={`px-2 py-1 text-xs rounded border ${
                kind === opt.value
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-gray-700 border-stone-300'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading parking…</div>
      ) : (available || []).length === 0 && selectedIds.length === 0 ? (
        <div className="text-sm text-gray-500">No free parking units for this filter.</div>
      ) : (
        <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
          {(available || []).map((p) => {
            const id = Number(p.id);
            const checked = selectedIds.includes(id);
            const label = [
              p.unitName,
              p.blockName,
              p.levelName,
              p.accommodation?.unitType || p.accommodation?.name
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={id}
                className="flex flex-wrap items-center justify-between gap-2 rounded bg-white border border-stone-200 px-2 py-1.5">
                <label className="flex items-center gap-2 text-sm cursor-pointer min-w-0">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={checked}
                    onChange={() => toggleParking(id)}
                  />
                  <span className="truncate">{label}</span>
                  {p.cost ? (
                    <span className="text-xs text-gray-500 shrink-0">
                      ₹{Number(p.cost).toLocaleString('en-IN')}
                    </span>
                  ) : null}
                </label>
                {checked ? (
                  <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={!!includeCostById[id]}
                      onChange={() => toggleIncludeCost(id)}
                    />
                    Include cost
                  </label>
                ) : null}
              </div>
            );
          })}

          {/* Selected items filtered out of available (shouldn't happen) still show */}
          {selectedIds
            .filter((id) => !available?.some((p) => Number(p.id) === Number(id)))
            .map((id) => {
              const p = optionMap.get(Number(id));
              if (!p) return null;
              return (
                <div
                  key={`sel-${id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded bg-white border border-stone-200 px-2 py-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked
                      onChange={() => toggleParking(id)}
                    />
                    <span>{p.unitName}</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={!!includeCostById[id]}
                      onChange={() => toggleIncludeCost(id)}
                    />
                    Include cost
                  </label>
                </div>
              );
            })}
        </div>
      )}

      {selectedIds.length > 0 ? (
        <div className="mt-2 text-xs text-gray-600">
          {selectedIds.length} parking linked
          {Object.values(includeCostById).some(Boolean)
            ? ` · cost included for ${Object.values(includeCostById).filter(Boolean).length}`
            : ''}
        </div>
      ) : null}
    </div>
  );
}

export function sumIncludedParkingCost(parkingSelection) {
  if (!parkingSelection?.parking_unit_ids?.length) return 0;
  return parkingSelection.parking_unit_ids.reduce((sum, id) => {
    if (!parkingSelection.include_cost_by_parking_id?.[id]) return sum;
    return sum + (Number(parkingSelection.parking_costs_by_id?.[id]) || 0);
  }, 0);
}
